package com.partejar.app.feature.planos.data.remote

import com.partejar.app.core.network.ApiConfig
import com.partejar.app.core.session.CurrentWorkspaceRepository
import com.partejar.app.feature.planos.data.Plano
import com.partejar.app.feature.planos.data.PlanoInput
import com.partejar.app.feature.planos.data.PlanosAccessException
import com.partejar.app.feature.planos.data.PlanosDataException
import com.partejar.app.feature.planos.data.PlanosDataSource
import com.partejar.app.feature.planos.data.PlanosUnavailableException
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import kotlinx.coroutines.CancellationException

class PlanosRemoteDataSource(
    private val client: HttpClient,
    private val apiConfig: ApiConfig,
    private val currentWorkspaceRepository: CurrentWorkspaceRepository,
    private val mapper: PlanosRemoteMapper
) : PlanosDataSource {
    override suspend fun list(): List<Plano> = request {
        val response = client.get(apiConfig.endpoint("/planos")) { workspaceHeader() }
        when (response.status) {
            HttpStatusCode.OK -> response.body<List<PlanoDto>>().map(mapper::map)
            HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden -> throw PlanosAccessException()
            else -> throw PlanosUnavailableException()
        }
    }

    override suspend fun create(input: PlanoInput): Plano = request {
        val response = client.post(apiConfig.endpoint("/planos")) {
            workspaceHeader()
            contentType(ContentType.Application.Json)
            setBody(CriarPlanoDto(input.name.trim(), input.currentPrice, input.services.map(::PlanoServicoRequestDto)))
        }
        when (response.status) {
            HttpStatusCode.Created -> mapper.map(response.body())
            HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden -> throw PlanosAccessException()
            else -> throw PlanosUnavailableException()
        }
    }

    override suspend fun update(planId: Long, input: PlanoInput): Plano = request {
        val response = client.patch(apiConfig.endpoint("/planos/$planId")) {
            workspaceHeader()
            contentType(ContentType.Application.Json)
            setBody(AtualizarPlanoDto(input.name.trim(), input.currentPrice, input.services.map(::PlanoServicoRequestDto), input.active))
        }
        when (response.status) {
            HttpStatusCode.OK -> mapper.map(response.body())
            HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden -> throw PlanosAccessException()
            else -> throw PlanosUnavailableException()
        }
    }

    private fun io.ktor.client.request.HttpRequestBuilder.workspaceHeader() {
        val workspaceId = currentWorkspaceRepository.currentWorkspaceId() ?: throw PlanosAccessException()
        header("X-Workspace-Id", workspaceId)
    }

    private suspend fun <T> request(block: suspend () -> T): T = try {
        block()
    } catch (error: CancellationException) {
        throw error
    } catch (error: PlanosDataException) {
        throw error
    } catch (error: Throwable) {
        throw PlanosUnavailableException(error)
    }
}
